import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Radar, AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";

const STATUS_STYLE = {
  excellent: "bg-deepgreen text-deepgreen-foreground",
  good: "bg-deepgreen/10 text-deepgreen border-deepgreen/30",
  attention: "bg-gold/15 text-gold border-gold/30",
  critical: "bg-destructive/10 text-destructive border-destructive/30",
  insufficient: "bg-muted text-muted-foreground",
};

export default function Situation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  async function refresh() {
    setLoading(true);
    try { const { data } = await api.get("/situation"); setData(data); } catch {}
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);
  if (!data) return <div className="p-8 subtle">Loading situation room…</div>;
  const overall = data.overall_status || "insufficient";
  return (
    <div className="space-y-6" data-testid="situation-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Executive</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Situation Room</h1>
          <p className="subtle mt-1">Real-time operational command centre — configurable scoring & drill-down.</p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      <Card className="rounded-2xl overflow-hidden">
        <div className={`h-2 ${overall === "critical" ? "bg-destructive" : overall === "attention" ? "bg-gold" : overall === "insufficient" ? "bg-muted" : "bg-deepgreen"}`} />
        <CardContent className="pt-6">
          <div className="flex items-baseline gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Overall operational score</div>
              <div className="mt-1 font-heading text-6xl font-semibold tracking-tight" data-testid="overall-score">
                {data.overall_score !== null ? data.overall_score : "—"}
              </div>
            </div>
            <Badge className={`px-3 py-1 text-xs uppercase tracking-widest ${STATUS_STYLE[overall]}`}>{overall}</Badge>
          </div>
          <Progress value={data.overall_score || 0} className="mt-4" />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(data.components || []).map((c) => (
          <Card key={c.key} className="rounded-2xl" data-testid={`comp-${c.key}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading">{c.label}</CardTitle>
                <Badge variant="outline" className={STATUS_STYLE[c.status]}>{c.status}</Badge>
              </div>
              <CardDescription className="text-xs">weight {c.weight}%</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="font-heading text-3xl font-semibold tracking-tight">{c.score !== null ? c.score : "—"}</div>
              <p className="subtle mt-2 text-xs">{c.detail}</p>
              {c.calc && Object.keys(c.calc).length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Calculation</summary>
                  <pre className="whitespace-pre-wrap mt-1 font-mono text-[11px]">{JSON.stringify(c.calc, null, 2)}</pre>
                </details>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {data.critical_alerts?.length > 0 && (
        <Card className="rounded-2xl border-destructive/40">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" />Critical alerts</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {data.critical_alerts.map((n) => (
                <li key={n.id} className="py-2 text-sm">
                  <div className="font-medium">{n.title}</div>
                  <div className="subtle">{n.message}</div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
